import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { INSURANCE_USAGE_GRIDS } from 'features/insuranceConventions/edit/queries';
import { TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Button, FakeText, Typography } from '@totalsoft/rocket-ui';
import { StyledTable, StyledActionHeaderCell, StyledHeaderCell, StyledGrayIcon, StyledTableContainer } from 'components/common/Styles';
import { useTranslation } from 'react-i18next';
import InsuranceConventionUsageGridComponent from './InsuranceConventionUsageGridComponent';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { ADD_UPDATE_INSURANCE_USAGE_GRID, DELETE_INSURANCE_USAGE_GRID } from 'features/insuranceConventions/edit/mutations';
import { useToast } from 'hooks/toasts/useToast';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { INSURANCE_USAGE_GRID_DELETED, INSURANCE_USAGE_GRID_SAVED } from 'features/insuranceConventions/edit/subscriptions';
import { extractGraphQLErrorMessage, sortBy } from 'utils/functions';
import { addCachedDetail, removeCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import FlexBox from 'components/common/FlexBox';

const InsuranceConventionUsageGridsContainer = ({ productId, categoryId, adding, setAdding }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const confirm = useConfirmDialog();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const [usageGrids, setUsageGrids] = useState([]);

  const hasCategory = useMemo(() => Boolean(categoryId), [categoryId]);

  const { loading } = useQuery(INSURANCE_USAGE_GRIDS, {
    skip: !productId || !categoryId,
    variables: { productId, categoryId },
    onCompleted: data => {
      setUsageGrids(data?.insuranceUsageGrids || []);
    }
  });

  const updateLocal = useCallback(
    (id, property, value) => {
      setUsageGrids(prev => {
        const modifyUsageGrid = usageGrid => {
          const newUsageGrid = { ...usageGrid };
          newUsageGrid[property] = value;
          if (property === 'editing' && !value) newUsageGrid.disabled = false;
          return newUsageGrid;
        };
        const newUsageGrids = prev.map(x => (x.productId === productId && x.id === id ? modifyUsageGrid(x) : x));
        return newUsageGrids;
      });
    },
    [productId]
  );

  const handleAdd = useCallback(() => {
    setUsageGrids(prev => [
      { isNew: true, id: null, productId, categoryId, age: null, usagePercent: null, insurancePercent: null },
      ...prev
    ]);
    setAdding(true);
  }, [productId, categoryId, setAdding]);

  const handleCancelAdd = useCallback(() => {
    setUsageGrids(prev => prev.filter((_, i) => i !== 0));
    setAdding(false);
  }, [setAdding]);

  // #region Mutate

  const [mutateUsageGrid] = useMutation(ADD_UPDATE_INSURANCE_USAGE_GRID, {
    onError: (errorObj, mutation) => {
      if (mutation) {
        updateLocal(mutation.variables?.input?.id, 'disabled', false);
        addToast(extractGraphQLErrorMessage(errorObj), 'error');
      } else updateLocal(errorObj.params?.id, 'disabled', false);
    }
  });

  const handleMutate = useCallback(
    usageGrid => {
      if (usageGrids.some(x => x.age === usageGrid.age && x.id !== usageGrid.id)) {
        addToast(t('InsuranceConventions.Details.UsageTable.AgeExists'), 'error');
        return;
      }

      updateLocal(usageGrid.id, 'disabled', true);

      // eslint-disable-next-line no-unused-vars
      const { isNew, key, editing, disabled, categoryName, ...sanitizedUsageGrid } = usageGrid;

      mutateUsageGrid({
        variables: { input: sanitizedUsageGrid },
        errorHandlingParams: { id: usageGrid.id }
      });
    },
    [addToast, usageGrids, t, updateLocal, mutateUsageGrid]
  );

  useSubscription(INSURANCE_USAGE_GRID_SAVED, {
    onData: ({
      data: {
        data: {
          insuranceUsageGridSaved: { isNew, usageGrid }
        }
      },
      client
    }) => {
      if (isNew) {
        handleCancelAdd();
        setUsageGrids(prev => {
          const newUsageGrids = [...prev, usageGrid];
          sortBy(newUsageGrids, ['age']);
          return newUsageGrids;
        });
      } else {
        setUsageGrids(prev => {
          const newUsageGrids = prev.map(x => (x.id === usageGrid.id ? { ...usageGrid, editing: false, disabled: false } : x));

          sortBy(newUsageGrids, ['age']);
          return newUsageGrids;
        });
      }
      addCachedDetail(
        client,
        { name: 'insuranceUsageGrids', value: INSURANCE_USAGE_GRIDS },
        { productId, categoryId },
        x => x.productId === usageGrid.productId && x.categoryId === usageGrid.categoryId && x.id === usageGrid.id,
        usageGrid,
        ['age']
      );
      addToast(t('InsuranceConventions.Details.UsageTable.Saved'), 'success');
    }
  });

  // #endregion

  // #region Delete

  const [deleteUsageGrid] = useMutation(DELETE_INSURANCE_USAGE_GRID, {
    onError: ({ params }) => {
      updateLocal(params.id, 'disabled', false);
    }
  });

  const handleDelete = useCallback(
    id => {
      confirm(
        () => {
          updateLocal(id, 'disabled', true);
          deleteUsageGrid({
            variables: { id },
            errorHandlingParams: { id }
          });
        },
        {
          title: t('InsuranceConventions.Details.UsageTable.Delete'),
          content: t('InsuranceConventions.Details.UsageTable.AreYouSureDelete')
        }
      );
    },
    [deleteUsageGrid, confirm, t, updateLocal]
  );

  useSubscription(INSURANCE_USAGE_GRID_DELETED, {
    onData: ({
      data: {
        data: {
          insuranceUsageGridDeleted: { usageGridId }
        }
      },
      client
    }) => {
      setUsageGrids(prev => prev.filter(x => x.id !== usageGridId));
      removeCachedDetail(
        client,
        { name: 'insuranceUsageGrids', value: INSURANCE_USAGE_GRIDS },
        { productId, categoryId },
        x => x.id === usageGridId,
        ['age']
      );
      addToast(t('InsuranceConventions.Details.UsageTable.Deleted'), 'success');
    }
  });

  // #endregion

  return (
    <StyledTableContainer>
      <StyledTable minWidth={'700px'}>
        <TableHead>
          <TableRow>
            <StyledHeaderCell>{t('InsuranceConventions.Details.UsageTable.Age')}</StyledHeaderCell>
            <StyledHeaderCell>{t('InsuranceConventions.Details.UsageTable.UsagePercent')} (%)</StyledHeaderCell>
            <StyledHeaderCell>{t('InsuranceConventions.Details.UsageTable.InsurancePercent')} (%)</StyledHeaderCell>
            <StyledActionHeaderCell align='right'>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAdd}
                disabled={adding || !categoryId || loading || !canWrite}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t('Add')}
              </Button>
            </StyledActionHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {hasCategory && loading && (
              <TableCell colSpan={4}>
                <FakeText lines={5} />
              </TableCell>
            )}
            {hasCategory && !loading && usageGrids.length === 0 && (
              <TableCell colSpan={4}>
                <FlexBox gap={1} alignItems='center' justifyContent='center'>
                  <StyledGrayIcon fontSize='small'>error</StyledGrayIcon>
                  <Typography color='textSecondary'>{t('InsuranceConventions.Details.AddValues')}</Typography>
                </FlexBox>
              </TableCell>
            )}
            {!hasCategory && (
              <TableCell colSpan={4}>
                <FlexBox gap={1} alignItems='center' justifyContent='center'>
                  <StyledGrayIcon fontSize='small'>error</StyledGrayIcon>
                  <Typography color='textSecondary'>{t('InsuranceConventions.Details.UsageTable.SelectCategory')}</Typography>
                </FlexBox>
              </TableCell>
            )}
          </TableRow>
          {!loading &&
            usageGrids.length > 0 &&
            usageGrids.map(usageGrid => (
              <InsuranceConventionUsageGridComponent
                key={usageGrid.id}
                usageGrid={usageGrid}
                onUpdateLocal={updateLocal}
                onMutate={handleMutate}
                onDelete={handleDelete}
                onCancelAdd={handleCancelAdd}
              />
            ))}
        </TableBody>
      </StyledTable>
    </StyledTableContainer>
  );
};

InsuranceConventionUsageGridsContainer.propTypes = {
  productId: PropTypes.number,
  categoryId: PropTypes.number,
  adding: PropTypes.bool,
  setAdding: PropTypes.func
};

export default InsuranceConventionUsageGridsContainer;
